import {
  ConfigAssetPoints,
  getPointsLabel,
  MerklRewardLegacy,
  PointsItem,
} from "@silo/types";
import { classNames } from "../../utils/class-names";
import { Perk } from ".";
import { SuperfestIcon } from "../../icons";
import { Array, pipe } from "effect";
import { PointsIcon } from "./points-icon";

export interface MarketCardPerksAsset {
  symbol: string;
  points: ConfigAssetPoints;
  merklRewards: MerklRewardLegacy[];
}

export interface MarketCardPerksProps {
  className?: string;
  assets: MarketCardPerksAsset[];
}

export function MarketCardPerks({
  className,
  assets,
}: MarketCardPerksProps): JSX.Element | null {
  const assetsWithPerks = pipe(
    assets,
    Array.map(mapToAsset),
    Array.filter((asset) => {
      return (
        asset.hasDepositSuperFestRewards ||
        asset.hasDebtSuperFestRewards ||
        asset.hasDepositPoints ||
        asset.hasDebtPoints
      );
    })
  );

  if (assetsWithPerks.length === 0) {
    return null;
  }

  return (
    <div className={classNames("flex flex-col gap-5", className)}>
      {assetsWithPerks.map((asset) => (
        <AssetPerks asset={asset} />
      ))}
    </div>
  );
}

interface Asset {
  symbol: string;
  hasDepositSuperFestRewards: boolean;
  hasDebtSuperFestRewards: boolean;
  depositPoints: PointsItem[];
  debtPoints: PointsItem[];
  hasDepositPoints: boolean;
  hasDebtPoints: boolean;
}

interface AssetPerksProps {
  asset: Asset;
}

function AssetPerks({ asset }: AssetPerksProps): JSX.Element {
  const {
    symbol,
    hasDepositSuperFestRewards,
    hasDebtSuperFestRewards,
    depositPoints,
    debtPoints,
    hasDepositPoints,
    hasDebtPoints,
  } = asset;

  const hasDepositPerks = hasDepositSuperFestRewards || hasDepositPoints;
  const hasDebtPerks = hasDebtSuperFestRewards || hasDebtPoints;

  return (
    <>
      {hasDepositPerks && (
        <Perk.Root>
          <Perk.Label>{symbol} depositors get:</Perk.Label>

          {hasDepositSuperFestRewards && <SuperfestRewards />}

          {hasDepositPoints &&
            depositPoints.map((pointsItem) => (
              <PointsPerk pointsItem={pointsItem} />
            ))}
        </Perk.Root>
      )}

      {hasDebtPerks && (
        <Perk.Root>
          <Perk.Label>{symbol} borrowers get:</Perk.Label>

          {hasDebtSuperFestRewards && <SuperfestRewards />}

          {hasDebtPoints &&
            debtPoints.map((pointsItem) => (
              <PointsPerk pointsItem={pointsItem} />
            ))}
        </Perk.Root>
      )}
    </>
  );
}

interface SuperfestRewardsProps {
  className?: string;
}

function SuperfestRewards({ className }: SuperfestRewardsProps): JSX.Element {
  return (
    <Perk.Item className={className}>
      <SuperfestIcon className={Perk.iconClassName} />

      <Perk.Emphasis>SuperFest</Perk.Emphasis>

      <Perk.RewardsLabel />
    </Perk.Item>
  );
}

interface PointsPerkProps {
  className?: string;
  pointsItem: PointsItem;
}

function PointsPerk({ className, pointsItem }: PointsPerkProps): JSX.Element {
  const pointsKey = pointsItem._tag;
  const label = getPointsLabel(pointsItem);

  return (
    <Perk.Item className={className}>
      <PointsIcon className={Perk.iconClassName} pointsKey={pointsKey} />

      <Perk.Emphasis>{label}</Perk.Emphasis>

      <Perk.PointsLabel />
    </Perk.Item>
  );
}

function mapToAsset(asset: MarketCardPerksAsset): Asset {
  const { symbol, merklRewards, points } = asset;

  const depositSuperFestRewards = merklRewards.filter((reward) => {
    const { type, isSuperfest } = reward;

    return isSuperfest && (type === "collateral" || type === "protected");
  });

  const debtSuperFestRewards = merklRewards.filter((reward) => {
    const { type, isSuperfest } = reward;

    return isSuperfest && type === "debt";
  });

  const depositPoints = [
    ...(points.collateral ?? []),
    ...(points.protected ?? []),
  ];

  const debtPoints = [...(points.debt ?? [])];

  return {
    symbol,
    hasDepositSuperFestRewards: depositSuperFestRewards.length > 0,
    hasDebtSuperFestRewards: debtSuperFestRewards.length > 0,
    depositPoints,
    debtPoints,
    hasDepositPoints: depositPoints.length > 0,
    hasDebtPoints: debtPoints.length > 0,
  };
}
